import { NEUTRAL_STYLE, UNASSIGNED_STYLE, type MemberStyle, type MemberView } from './members';

/**
 * The calendar glossary.
 *
 * Usability testing 2026-08-01: neither tester could decode the grid — "what does orange
 * mean?" (it means *today's birthday underline and the now-line*, never a person). The "?"
 * chip beside the view switcher opens a panel built from this list, so every mark on the
 * grid is named exactly once, with a live sample rendered from the very same classes the
 * grid uses. Member rows come from the live household roster, so a new member appears in
 * the legend the moment their profile syncs.
 */

export type LegendSample = 'dot' | 'hollow-dot' | 'birthday' | 'today' | 'now-line' | 'more';

export interface LegendEntry {
  id: string;
  label: string;
  meaning: string;
  sample: LegendSample;
  /** Present for the sample kinds that take a member colour. */
  style?: MemberStyle;
}

export function buildLegend(members: readonly MemberView[]): LegendEntry[] {
  const memberEntries: LegendEntry[] = members.map((member) => ({
    id: `member-${member.id}`,
    label: member.name,
    meaning: `${member.name}'s events`,
    sample: 'dot',
    style: member.style,
  }));

  return [
    ...memberEntries,
    {
      id: 'unassigned',
      label: 'Nobody / imported',
      meaning: 'Hollow — an event with no person on it, like anything pulled from a feed.',
      sample: 'hollow-dot',
      style: UNASSIGNED_STYLE,
    },
    {
      id: 'unknown',
      label: 'Not synced yet',
      meaning: "Solid grey — someone's on it, but their profile hasn't reached this device.",
      sample: 'dot',
      style: NEUTRAL_STYLE,
    },
    {
      id: 'today',
      label: 'Today',
      meaning: 'The filled date badge. It stays put whatever the filter is set to.',
      sample: 'today',
    },
    {
      id: 'now-line',
      label: 'Now',
      meaning: "The accent rule across today's column in week view — the current time.",
      sample: 'now-line',
    },
    {
      id: 'birthday',
      label: 'Birthday',
      meaning:
        'An accent ring (and an underlined date). Accent means birthday or now — never a person.',
      sample: 'birthday',
    },
    {
      id: 'more',
      label: '+N',
      meaning: 'More on that day than the cell can draw. Tap the day to see all of it.',
      sample: 'more',
    },
  ];
}
